/**
 * Pure role-based permission helpers for the frontend.
 *
 * The backend is the source of truth — these helpers only decide what
 * to render. Each predicate is a pure function over ``CurrentUser`` so
 * it composes anywhere without React hook ergonomics.
 *
 * Consume via:
 *   const { data: user } = useCurrentUser()
 *   if (canCreateInvoice(user)) { ... }
 */

import type { CurrentUser, Role } from "./types"

interface InvoiceLike {
  status?: string
  created_by?: { id: number } | null
  createdBy?: { id: number } | null
}

const ROLE_SALES_REP: Role = "sales_representative"
const ROLE_MANAGER: Role = "manager"
const ROLE_FINANCE: Role = "finance"

function getRole(user: CurrentUser | null | undefined): Role | null {
  if (!user) return null
  if (user.is_superuser) return ROLE_MANAGER
  return user.role ?? null
}

export function isReadOnly(user: CurrentUser | null | undefined): boolean {
  return getRole(user) === ROLE_FINANCE
}

export function isSalesRep(user: CurrentUser | null | undefined): boolean {
  return getRole(user) === ROLE_SALES_REP
}

export function isManager(user: CurrentUser | null | undefined): boolean {
  return getRole(user) === ROLE_MANAGER
}

/**
 * Whether the user may modify a live installment plan (pause / extend /
 * change-amount / reschedule) from the Edit-invoice "Installment plan" tab.
 * Managers only (instalments-modification).
 */
export function canModifyInstallmentPlan(
  user: CurrentUser | null | undefined,
): boolean {
  return isManager(user)
}

export function hasRole(user: CurrentUser | null | undefined): boolean {
  return getRole(user) !== null
}

export function canCreateInvoice(
  user: CurrentUser | null | undefined,
): boolean {
  const role = getRole(user)
  return role === ROLE_SALES_REP || role === ROLE_MANAGER
}

/**
 * True iff the user may pick any other creator on the invoice Owner
 * filter (Manager or Finance). Sales Reps are server-side scoped to
 * their own invoices and don't see the picker; no-role users render
 * no filters at all.
 */
export function canFilterByAnyOwner(
  user: CurrentUser | null | undefined,
): boolean {
  const role = getRole(user)
  return role === ROLE_MANAGER || role === ROLE_FINANCE
}

function ownsInvoice(
  user: CurrentUser | null | undefined,
  invoice: InvoiceLike | null | undefined,
): boolean {
  if (!user) return false
  const creator = invoice?.created_by ?? invoice?.createdBy
  if (!creator) return false
  return creator.id === user.id
}

export function canEditInvoice(
  user: CurrentUser | null | undefined,
  invoice: InvoiceLike | null | undefined,
): boolean {
  const role = getRole(user)
  if (role === null || role === ROLE_FINANCE) return false
  if (role === ROLE_SALES_REP && !ownsInvoice(user, invoice)) return false
  return true
}

export function canSendInvoice(
  user: CurrentUser | null | undefined,
  invoice: InvoiceLike | null | undefined,
): boolean {
  return canEditInvoice(user, invoice)
}

export function canVoid(
  user: CurrentUser | null | undefined,
  invoice: InvoiceLike | null | undefined,
): boolean {
  return canEditInvoice(user, invoice)
}

/**
 * True iff the user may delete this (draft) invoice: not Finance, and a
 * Sales Rep only for their own — a Manager may delete any. Identical rule
 * to ``canEditInvoice`` (the requirement's SR-own / Manager-any wording);
 * aliased so the delete gate can never drift from the edit gate.
 */
export function canDelete(
  user: CurrentUser | null | undefined,
  invoice: InvoiceLike | null | undefined,
): boolean {
  return canEditInvoice(user, invoice)
}
